/**
 * QuotationsListPage — iter-290 (Feb 2026)
 * ========================================
 * Lists every quotation, newest first. Supports:
 *   - Text search (quotation_no / customer_name / customer_company / title)
 *   - Status filter
 *   - Edit / Delete / Print actions per row
 *
 * Route: /manage/cloudgm-fz-llc/quotations/list
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus, Search, Loader2, Pencil, Trash2, Printer, FileText,
  AlertTriangle,
} from 'lucide-react';

import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import DashboardLayout from '../../components/DashboardLayout';
import { useLanguage } from '../../context/LanguageContext';
import { getAuthHeaders } from '../../utils/authHeaders';
import { printQuotation } from '../../utils/printQuotation';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  draft:    { ar: 'مسودة',     en: 'Draft',    color: '#6b7280' },
  sent:     { ar: 'مرسل',      en: 'Sent',     color: '#3B9EDC' },
  accepted: { ar: 'مقبول',     en: 'Accepted', color: '#10b981' },
  rejected: { ar: 'مرفوض',     en: 'Rejected', color: '#ef4444' },
  expired:  { ar: 'منتهي',     en: 'Expired',  color: '#f59e0b' },
};

export default function QuotationsListPage() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const t = useCallback((ar, en) => (isAr ? ar : en), [isAr]);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  // iter-295 — Print language picker opens before every print action
  // (list page + edit page), so the user always chooses AR / EN / Both.
  const [printTarget, setPrintTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search.trim()) params.search = search.trim();
      if (statusFilter !== 'all') params.status = statusFilter;
      const r = await axios.get(`${API}/admin/quotations`,
                                 { params, headers: getAuthHeaders() });
      setRows(r.data.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  }, [search, statusFilter]);

  // Debounced reload when the search box changes.
  useEffect(() => {
    const h = setTimeout(load, 300);
    return () => clearTimeout(h);
  }, [load]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/quotations/${deleteTarget.id}`,
                          { headers: getAuthHeaders() });
      toast.success(t('تم حذف عرض السعر', 'Quotation deleted'));
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setDeleting(false); }
  };

  const breadcrumb = useMemo(() => ([
    { labelAr: 'لوحة التحكم', labelEn: 'Dashboard', path: '/dashboard' },
    { labelAr: 'CLOUDGM FZ LLC', labelEn: 'CLOUDGM FZ LLC', path: '/dashboard' },
    { labelAr: 'إدارة شركة CLOUDGM FZ LLC', labelEn: 'Management', path: '/manage/cloudgm-fz-llc' },
    { labelAr: 'عروض الأسعار', labelEn: 'Quotations', path: '/manage/cloudgm-fz-llc/quotations' },
    { labelAr: 'المتابعة', labelEn: 'List' },
  ]), []);

  return (
    <DashboardLayout
      title="متابعة عروض الأسعار"
      titleEn="Manage Quotations"
      subtitle="بحث وتعديل وطباعة كل عروض الأسعار"
      subtitleEn="Search, edit and print every quotation"
      backPath="/manage/cloudgm-fz-llc/quotations"
      onBack={() => navigate('/manage/cloudgm-fz-llc/quotations')}
      breadcrumb={breadcrumb}
    >
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="quotations-list-page">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
          <div className="flex items-center gap-2 flex-1 min-w-[280px] max-w-md">
            <div className="relative flex-1">
              <Search className="absolute top-1/2 -translate-y-1/2 start-3 h-4 w-4 text-white/40" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)}
                     placeholder={t('ابحث بالرقم أو اسم العميل…', 'Search by number or customer…')}
                     className="ps-9 bg-black/30 border-white/10 text-sm"
                     dir={isAr ? 'rtl' : 'ltr'}
                     data-testid="quotations-search" />
            </div>
            <select value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="bg-black/30 border border-white/10 rounded-md px-3 h-9 text-sm text-white"
                    data-testid="quotations-status-filter">
              <option value="all">{t('كل الحالات', 'All statuses')}</option>
              {Object.entries(STATUS_META).map(([k, m]) => (
                <option key={k} value={k}>{isAr ? m.ar : m.en}</option>
              ))}
            </select>
          </div>
          <Button onClick={() => navigate('/manage/cloudgm-fz-llc/quotations/create')}
                  className="bg-[#10b981] hover:bg-[#059669] text-black font-semibold"
                  data-testid="quotations-new-btn">
            <Plus className="h-4 w-4 me-1.5" />
            {t('عرض جديد', 'New quotation')}
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-40">
            <Loader2 className="h-6 w-6 animate-spin text-white/40" />
          </div>
        ) : rows.length === 0 ? (
          <div className="bg-white/[0.02] border border-dashed border-white/10 rounded-2xl p-10 text-center"
               data-testid="quotations-empty">
            <FileText className="h-10 w-10 text-white/20 mx-auto mb-3" />
            <p className="text-white/60 text-sm mb-4">
              {search || statusFilter !== 'all'
                ? t('لا توجد نتائج تطابق البحث', 'No results match your filters')
                : t('لم يتم إنشاء أي عرض سعر بعد', 'No quotations have been created yet')}
            </p>
            <Button onClick={() => navigate('/manage/cloudgm-fz-llc/quotations/create')}
                    className="bg-[#10b981] hover:bg-[#059669] text-black font-semibold">
              <Plus className="h-4 w-4 me-1.5" />
              {t('إنشاء أول عرض', 'Create the first quotation')}
            </Button>
          </div>
        ) : (
          <div className="bg-white/[0.02] border border-white/10 rounded-2xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-white/[0.03] border-b border-white/5 text-[11px] uppercase text-white/50">
                  <th className="text-start px-4 py-3">{t('رقم العرض', 'No.')}</th>
                  <th className="text-start px-4 py-3">{t('العميل', 'Customer')}</th>
                  <th className="text-start px-4 py-3 hidden md:table-cell">{t('العنوان', 'Title')}</th>
                  <th className="text-end px-4 py-3">{t('الإجمالي (AED)', 'Total (AED)')}</th>
                  <th className="text-center px-4 py-3">{t('الحالة', 'Status')}</th>
                  <th className="text-end px-4 py-3">{t('إجراءات', 'Actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5" data-testid="quotations-table-body">
                {rows.map((q) => {
                  const st = STATUS_META[q.status] || STATUS_META.draft;
                  const totMin = (q.totals?.total_min ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2 });
                  const totMax = (q.totals?.total_max ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2 });
                  return (
                    <tr key={q.id} className="hover:bg-white/[0.03] transition" data-testid={`quot-row-${q.quotation_no}`}>
                      <td className="px-4 py-3 font-mono text-[#3B9EDC] font-semibold text-xs" dir="ltr">
                        {q.quotation_no}
                      </td>
                      <td className="px-4 py-3 text-white/80">
                        <div className="truncate max-w-[180px]">{q.customer_name}</div>
                        {q.customer_company && (
                          <div className="text-[10px] text-white/40 truncate max-w-[180px]">{q.customer_company}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-white/60 hidden md:table-cell max-w-[200px]">
                        <div className="truncate">{isAr ? (q.title_ar || q.title_en || '—') : (q.title_en || q.title_ar || '—')}</div>
                      </td>
                      <td className="px-4 py-3 text-end text-white/80 tabular-nums whitespace-nowrap" dir="ltr">
                        {totMin === totMax ? totMax : `${totMin} - ${totMax}`}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold border"
                              style={{ color: st.color, borderColor: `${st.color}50`, backgroundColor: `${st.color}15` }}>
                          {isAr ? st.ar : st.en}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => setPrintTarget(q)}
                                  className="h-8 w-8 rounded-md hover:bg-white/5 flex items-center justify-center text-white/60 hover:text-white"
                                  title={t('طباعة', 'Print')}
                                  data-testid={`quot-print-${q.quotation_no}`}>
                            <Printer className="h-3.5 w-3.5" />
                          </button>
                          <button onClick={() => navigate(`/manage/cloudgm-fz-llc/quotations/edit/${q.id}`)}
                                  className="h-8 w-8 rounded-md hover:bg-white/5 flex items-center justify-center text-white/60 hover:text-[#3B9EDC]"
                                  title={t('تعديل', 'Edit')}
                                  data-testid={`quot-edit-${q.quotation_no}`}>
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                          <button onClick={() => setDeleteTarget(q)}
                                  className="h-8 w-8 rounded-md hover:bg-red-500/10 flex items-center justify-center text-white/60 hover:text-red-400"
                                  title={t('حذف', 'Delete')}
                                  data-testid={`quot-delete-${q.quotation_no}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* ─── Print mode picker ─── */}
      <AnimatePresence>
        {printTarget && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
                      onClick={() => setPrintTarget(null)}>
            <motion.div initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95 }}
                        className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6 w-full max-w-md"
                        onClick={(e) => e.stopPropagation()}
                        data-testid="list-print-picker">
              <div className="flex items-start gap-3 mb-4">
                <div className="h-10 w-10 rounded-xl bg-sky-500/15 flex items-center justify-center shrink-0">
                  <Printer className="h-5 w-5 text-sky-400" />
                </div>
                <div>
                  <h3 className="text-white font-bold">{t('اختر لغة الطباعة', 'Choose Print Language')}</h3>
                  <p className="text-white/50 text-xs mt-1" dir="ltr">{printTarget.quotation_no}</p>
                </div>
              </div>
              <div className="space-y-2">
                {[
                  { mode: 'ar',   label: 'عربي فقط',     sublabel: 'Arabic only' },
                  { mode: 'en',   label: 'إنجليزي فقط',  sublabel: 'English only' },
                  { mode: 'both', label: 'عربي + إنجليزي', sublabel: 'Both side-by-side', highlighted: true },
                ].map((o) => (
                  <button
                    key={o.mode}
                    onClick={() => { printQuotation(printTarget, { language, mode: o.mode }); setPrintTarget(null); }}
                    className={`w-full text-start px-4 py-3 rounded-lg border transition-colors ${
                      o.highlighted
                        ? 'border-[#F5A55A]/50 bg-[#F5A55A]/10 hover:bg-[#F5A55A]/20'
                        : 'border-white/10 bg-white/[0.02] hover:bg-white/[0.05]'
                    }`}
                    data-testid={`list-print-mode-${o.mode}`}
                  >
                    <div className="text-white font-semibold text-sm">{o.label}</div>
                    <div className="text-white/50 text-[11px]" dir="ltr">{o.sublabel}</div>
                  </button>
                ))}
              </div>
              <div className="flex items-center justify-end mt-5">
                <Button variant="outline" onClick={() => setPrintTarget(null)}
                        className="border-white/10 text-white/70">
                  {t('إلغاء', 'Cancel')}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ─── Delete confirmation ─── */}
      <AnimatePresence>
        {deleteTarget && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
                      onClick={() => !deleting && setDeleteTarget(null)}>
            <motion.div initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95 }}
                        className="bg-[#0a0a0a] border border-red-500/30 rounded-2xl p-6 w-full max-w-md"
                        onClick={(e) => e.stopPropagation()}
                        data-testid="quot-delete-modal">
              <div className="flex items-start gap-3 mb-4">
                <div className="h-10 w-10 rounded-xl bg-red-500/15 flex items-center justify-center shrink-0">
                  <AlertTriangle className="h-5 w-5 text-red-400" />
                </div>
                <div>
                  <h3 className="text-white font-bold">{t('حذف عرض السعر؟', 'Delete this quotation?')}</h3>
                  <p className="text-white/50 text-xs mt-1" dir="ltr">{deleteTarget.quotation_no}</p>
                </div>
              </div>
              <p className="text-white/70 text-sm mb-5">
                {t('هذا الإجراء لا يمكن التراجع عنه. سيتم حذف العرض نهائياً.',
                   'This cannot be undone. The quotation will be permanently deleted.')}
              </p>
              <div className="flex items-center justify-end gap-2">
                <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={deleting}
                        className="border-white/10 text-white/70">
                  {t('إلغاء', 'Cancel')}
                </Button>
                <Button onClick={handleDelete} disabled={deleting}
                        className="bg-red-500 hover:bg-red-600 text-white"
                        data-testid="quot-delete-confirm">
                  {deleting && <Loader2 className="h-3.5 w-3.5 me-1.5 animate-spin" />}
                  {t('حذف نهائي', 'Delete permanently')}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </DashboardLayout>
  );
}
